import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Fade,
  Stack,
  Typography,
  useTheme,
  Divider,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  PrecisionManufacturing as RobotIcon,
  Dashboard as DashboardIcon,
  ArrowBack as ArrowBackIcon,
  ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import Header from '../../common/Header';
import { motion } from 'framer-motion';

// ─── Design tokens — cyberpunk dark system ────────────────────────────────────
const B = {
  600: '#2563EB',
  700: '#1D4ED8',
  900: '#1E3A8A',
};

const NEON = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID = '#0a1628';
const SURFACE_UP = '#0f1f38';

const SCAN_LINES = `repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(14,165,233,0.016) 3px, rgba(14,165,233,0.016) 4px)`;

/* ─── Typing animation for the terminal line ─────────────────────── */
const useTypingEffect = (text, speed = 38, startDelay = 600) => {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let i = 0;
    const delay = setTimeout(() => {
      const interval = setInterval(() => {
        setDisplayed(text.slice(0, i + 1));
        i++;
        if (i >= text.length) { clearInterval(interval); setDone(true); }
      }, speed);
      return () => clearInterval(interval);
    }, startDelay);
    return () => clearTimeout(delay);
  }, [text, speed, startDelay]);

  return { displayed, done };
};

/* ─── Blinking cursor ─────────────────────────────────────────────── */
const Cursor = ({ visible }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: '2px',
      height: '1.1em',
      bgcolor: '#fff',
      ml: '3px',
      verticalAlign: 'middle',
      animation: visible ? 'blink 1s step-start infinite' : 'none',
      '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } },
    }}
  />
);

/* ─── Status chip ─────────────────────────────────────────────────── */
const StatusChip = ({ label, color }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.7,
      px: 1.4,
      py: 0.35,
      borderRadius: '4px',
      border: `1px solid ${alpha(color, 0.45)}`,
      background: alpha(color, 0.08),
      fontSize: '0.68rem',
      fontFamily: '"Courier New", monospace',
      fontWeight: 700,
      letterSpacing: '0.08em',
      color: color,
      textTransform: 'uppercase',
    }}
  >
    <Box
      sx={{
        width: 6, height: 6, borderRadius: '50%', bgcolor: color,
        animation: 'pulse 1.8s ease-in-out infinite',
        '@keyframes pulse': {
          '0%,100%': { opacity: 1, transform: 'scale(1)' },
          '50%': { opacity: 0.4, transform: 'scale(0.7)' },
        },
      }}
    />
    {label}
  </Box>
);

const MotionCard = motion(Card);

/* ─── Main component ──────────────────────────────────────────────── */
const InternalNotFound = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [mounted, setMounted] = useState(false);

  const terminalText = '> SCANNING SYSTEM DIRECTORY... RESOURCE NOT FOUND.';
  const { displayed, done } = useTypingEffect(terminalText, 34, 900);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(t);
  }, []);

  const glow = (opacity = 0.45) => `0 0 18px ${alpha(NEON, opacity)}`;

  // Card shell styling matching UserList, with top border line
  const cardShellSx = {
    borderRadius: '14px',
    background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.97)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    border: `1px solid ${alpha(NEON, 0.17)}`,
    borderTop: 'none', // Remove top border to let the pseudo-element act as the border
    boxShadow: [
      `0 0 0 1px ${alpha(B[900], 0.9)}`,
      `0 4px 24px ${alpha('#000', 0.45)}`,
      `0 20px 60px ${alpha('#000', 0.3)}`,
      `inset 0 1px 0 ${alpha(NEON, 0.1)}`,
    ].join(', '),
    backdropFilter: 'blur(20px)',
    overflow: 'hidden',
    position: 'relative',
    transition: 'box-shadow 0.28s ease, transform 0.28s ease',
    '&::before': {
      content: '""', 
      display: 'block', 
      height: '2px',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      background: `linear-gradient(90deg, transparent, ${alpha(NEON, 0.8)} 30%, ${alpha(NEON2, 0.9)} 50%, ${alpha(NEON, 0.8)} 70%, transparent)`,
      backgroundSize: '200% 100%',
      animation: 'bar-sweep 5s linear infinite',
      zIndex: 10,
    },
    '@keyframes bar-sweep': {
      '0%': { backgroundPosition: '200% 0' },
      '100%': { backgroundPosition: '-200% 0' },
    },
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

      {/* ── Consistent Header ─────────────────────────────────────── */}
      <Header
        title="System Error: 404"
        subtitle="Requested module not found in system directory"
        icon={ErrorOutlineIcon}
        showBackButton
        onBackClick={() => navigate('/dashboard')}
      />

      {/* ── Main Card ─────────────────────────────────────────────── */}
      <Fade in={mounted} timeout={700}>
        <MotionCard
          sx={{
            ...cardShellSx,
            mt: 3,
            textAlign: 'center',
            py: { xs: 5, sm: 7 },
            px: { xs: 2.5, sm: 6 },
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >

          {/* ── Status chips row ──────────────────────────────────── */}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 4, position: 'relative', zIndex: 1, gap: 1 }}
          >
            <StatusChip label="STATUS: OFFLINE" color={theme.palette.error.main} />
            <StatusChip label="MODULE: UNKNOWN" color={NEON} />
            <StatusChip label="SYS: ROBOCON v2.4" color={NEON3} />
          </Stack>

          {/* ── Robot icon ────────────────────────────────────────── */}
          <Box
            sx={{
              position: 'relative',
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3,
              zIndex: 1,
            }}
          >
            {/* outer ring */}
            <Box
              sx={{
                position: 'absolute',
                width: 120,
                height: 120,
                borderRadius: '50%',
                border: `1.5px solid ${alpha(NEON, 0.25)}`,
                animation: 'spin 12s linear infinite',
                '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
              }}
            />
            {/* inner ring */}
            <Box
              sx={{
                position: 'absolute',
                width: 96,
                height: 96,
                borderRadius: '50%',
                border: `1px dashed ${alpha(NEON, 0.18)}`,
                animation: 'spin 7s linear infinite reverse',
              }}
            />
            {/* icon glow disc */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: alpha(NEON, 0.1),
                boxShadow: glow(0.35),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RobotIcon
                sx={{
                  fontSize: 44,
                  color: NEON,
                  filter: `drop-shadow(${glow(0.7)})`,
                }}
              />
            </Box>
          </Box>

          {/* ── 404 ───────────────────────────────────────────────── */}
          <Typography
            sx={{
              fontFamily: '"Courier New", monospace',
              fontWeight: 900,
              fontSize: { xs: '5.5rem', sm: '8rem' },
              letterSpacing: '0.18em',
              lineHeight: 1,
              mb: 0.5,
              color: NEON,
              textShadow: `0 0 8px ${alpha(NEON, 0.45)}, 0 0 32px ${alpha(NEON, 0.2)}`,
              position: 'relative',
              zIndex: 1,
            }}
          >
            404
          </Typography>

          <Typography
            sx={{
              fontFamily: '"Courier New", monospace',
              fontWeight: 700,
              fontSize: { xs: '0.78rem', sm: '0.88rem' },
              letterSpacing: '0.3em',
              color: '#fff',
              mb: 3,
              textTransform: 'uppercase',
              zIndex: 1,
              position: 'relative',
            }}
          >
            ERROR · RESOURCE NOT FOUND
          </Typography>

          <Divider
            sx={{
              width: '60%',
              mx: 'auto',
              mb: 3,
              borderColor: alpha(NEON, 0.18),
            }}
          />

          {/* ── Terminal line ─────────────────────────────────────── */}
          <Box
            sx={{
              mx: 'auto',
              mb: 3.5,
              maxWidth: 500,
              px: 2.5,
              py: 1.4,
              borderRadius: '6px',
              background: alpha(SURFACE_BASE, 0.8),
              border: `1px solid ${alpha(NEON, 0.2)}`,
              textAlign: 'left',
              position: 'relative',
              zIndex: 1,
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Courier New", monospace',
                fontSize: '0.78rem',
                color: '#fff',
                letterSpacing: '0.04em',
                minHeight: '1.4em',
              }}
            >
              {displayed}
              <Cursor visible={done} />
            </Typography>
          </Box>

          {/* ── Description ───────────────────────────────────────── */}
          <Typography
            variant="body2"
            sx={{
              color: '#e2e8f0', // Light gray/white text
              maxWidth: 400,
              mx: 'auto',
              mb: 5,
              lineHeight: 1.8,
              zIndex: 1,
              position: 'relative',
            }}
          >
            The requested page could not be located in the system.
            <br />
            Please verify the path or return to a valid module.
          </Typography>

          {/* ── Buttons ───────────────────────────────────────────── */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ position: 'relative', zIndex: 1 }}
          >
            {/* Primary */}
            <Button
              variant="contained"
              size="large"
              startIcon={<DashboardIcon />}
              onClick={() => navigate('/dashboard')}
              sx={{
                borderRadius: 2,
                px: 3.5,
                py: 1.25,
                fontWeight: 700,
                fontFamily: '"Courier New", monospace',
                fontSize: '0.82rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#fff',
                background: `linear-gradient(135deg, ${B[600]}, ${B[700]})`,
                boxShadow: `0 4px 16px ${alpha(NEON, 0.4)}, 0 0 0 1px ${alpha(NEON, 0.3)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: `0 6px 24px ${alpha(NEON, 0.55)}, 0 0 0 1px ${alpha(NEON, 0.5)}`,
                  transform: 'translateY(-2px) scale(1.02)',
                  filter: 'brightness(1.08)',
                },
              }}
            >
              Return to Dashboard
            </Button>

            {/* Secondary */}
            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                px: 3.5,
                py: 1.25,
                fontWeight: 700,
                fontFamily: '"Courier New", monospace',
                fontSize: '0.82rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                borderColor: alpha(NEON, 0.4),
                color: '#fff',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: NEON,
                  background: alpha(NEON, 0.07),
                  boxShadow: `0 0 12px ${alpha(NEON, 0.2)}`,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Go Back
            </Button>
          </Stack>

          {/* ── Footer meta ───────────────────────────────────────── */}
          <Typography
            sx={{
              mt: 5,
              fontFamily: '"Courier New", monospace',
              fontSize: '0.65rem',
              letterSpacing: '0.12em',
              color: '#94a3b8', // Light slate
              textTransform: 'uppercase',
              zIndex: 1,
              position: 'relative',
            }}
          >
            ROBOCON SYSTEM · NODE: WEB-UI · CODE: 0x194
          </Typography>

        </MotionCard>
      </Fade>
    </Container>
  );
};

export default InternalNotFound;